import { useMemo } from "react";
import { Building2 } from "lucide-react";

import Select from "@/components/ui/Select";
import Skeleton from "@/components/shared/Skeleton";

/**
 * What to append to a tenant's name. Nothing for a live one — the common case
 * gets no decoration. A row with no state at all (pages that ask for active
 * tenants only) reads as live.
 */
const tenantStateSuffix = (tenant) => {
  if (tenant?.isDeleted) return " — closed";
  // "suspended", the word the rest of the app uses for a deactivated org —
  // the login toast, `org_suspended`, and every comment on the pages that
  // render this list. "InActive" was this component's own coinage.
  if (tenant?.isActive === false) return " — suspended";
  return "";
};

/**
 * The tenant a SuperAdmin is working inside, on pages that manage
 * tenant-owned data. Renders nothing for a tenant user — their JWT already
 * says which tenant they are in, so there is nothing to choose.
 *
 * Every row on the page belongs to the tenant named here, including the
 * ones in a dedicated database, which no cross-tenant list could reach.
 */
const TenantScopeSelect = ({ tenants, tenantId, onChange, isLoading, isSuperAdmin, label = "Working in", allowAll = false, allLabel = "All Tenants" }) => {
  const options = useMemo(() => {
    const list = (tenants ?? []).map((t) => ({
      // The tenant's name, and its state when that is not "live". The slug
      // used to lead the label, which made a 52px-wide trigger show the
      // identifier and truncate the one word the reader was looking for.
      // `useTenantScope` already falls back to the slug when a tenant has no
      // name, so this is never blank.
      //
      // The state is in the label rather than a colour because this list is
      // read to decide where to look: on Record Lookup it carries suspended
      // and closed organisations, and offering a closed one as though it were
      // live is how a SuperAdmin ends up filing against the wrong tenant.
      label: `${t.name || t.slug}${tenantStateSuffix(t)}`,
      value: t.id,
    }));
    if (allowAll) {
      return [{ label: allLabel, value: "ALL_TENANTS" }, ...list];
    }
    return list;
  }, [tenants, allowAll, allLabel]);

  if (!isSuperAdmin) return null;

  const selectedValue = allowAll && (!tenantId || tenantId === "ALL_TENANTS") ? "ALL_TENANTS" : tenantId;

  return (
    <div className="flex items-center gap-2 shrink-0">
      <span className="flex items-center gap-1.5 text-[11px] text-text-secondary whitespace-nowrap">
        <Building2 size={13} />
        {label}
      </span>

      {isLoading && !options.length ? (
        <Skeleton className="h-9 w-44 rounded-lg" />
      ) : (
        <Select
          value={selectedValue}
          onChange={(val) => {
            if (val === "ALL_TENANTS") {
              onChange?.("");
            } else {
              onChange?.(val);
            }
          }}
          options={options}
          placeholder="Select tenant"
          searchable={options.length > 8}
          searchPlaceholder="Search tenants..."
          className="w-52"
        />
      )}
    </div>
  );
};

export default TenantScopeSelect;
